// src/admin/pages/tradein/TradeInTable.tsx
import React from 'react';
import {
  Box,
  Chip,
  CircularProgress,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { TradeInSubmission } from '../../../enums/tradeIn';

interface TradeInTableProps {
  items: TradeInSubmission[];
  onEdit: (item: TradeInSubmission) => void;
  onDelete: (item: TradeInSubmission) => void;
  deleteLoading: string;
  isDark?: boolean;
  canManage?: boolean;
}

// ── Theme-aware chip style helper — mirrors BranchTable's getSoftChipSx ──────
const getSoftChipSx = (color: string, isDark: boolean = false) => {
  type PaletteEntry = { bg: string; border: string; text: string };

  const lightPalette: Record<string, PaletteEntry> = {
    warning: { bg: 'rgba(255,167,38,0.12)', border: 'rgba(255,167,38,0.4)', text: '#b76e00' },
    info: { bg: 'rgba(41,182,246,0.12)', border: 'rgba(41,182,246,0.4)', text: '#0277bd' },
    success: { bg: 'rgba(102,187,106,0.15)', border: 'rgba(102,187,106,0.45)', text: '#2e7d32' },
    error: { bg: 'rgba(239,83,80,0.12)', border: 'rgba(239,83,80,0.4)', text: '#c62828' },
    default: { bg: 'rgba(158,158,158,0.12)', border: 'rgba(158,158,158,0.4)', text: '#424242' },
  };

  const darkPalette: Record<string, PaletteEntry> = {
    warning: { bg: 'rgba(255,167,38,0.22)', border: 'rgba(255,167,38,0.55)', text: '#ffcc80' },
    info: { bg: 'rgba(41,182,246,0.20)', border: 'rgba(41,182,246,0.50)', text: '#81d4fa' },
    success: { bg: 'rgba(102,187,106,0.22)', border: 'rgba(102,187,106,0.50)', text: '#a5d6a7' },
    error: { bg: 'rgba(239,83,80,0.22)', border: 'rgba(239,83,80,0.50)', text: '#ef9a9a' },
    default: { bg: 'rgba(158,158,158,0.22)', border: 'rgba(158,158,158,0.50)', text: '#e0e0e0' },
  };

  const palette = isDark ? darkPalette : lightPalette;
  const p = palette[color] ?? palette.default;

  return {
    backgroundColor: p.bg,
    borderColor: p.border,
    color: p.text,
    fontWeight: 600,
    border: `1px solid ${p.border}`,
    '& .MuiChip-label': { color: p.text },
  };
};

const STATUS_CHIP_COLOR: Record<string, string> = {
  'New Submission': 'info',
  'Under Review': 'warning',
  'Offer Made': 'warning',
  Accepted: 'success',
  Rejected: 'error',
};

const TradeInTable: React.FC<TradeInTableProps> = ({
  items,
  onEdit,
  onDelete,
  deleteLoading,
  isDark = false,
  canManage = false,
}) => {
  return (
    <TableContainer
      component={Paper}
      sx={{
        height: '100%',
        '& .MuiTable-root': { minWidth: 900 },
      }}
    >
      <Table stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell>Owner</TableCell>
            <TableCell>Car</TableCell>
            <TableCell>Year</TableCell>
            <TableCell>Mileage</TableCell>
            <TableCell>Asking Price</TableCell>
            <TableCell>Status</TableCell>
            <TableCell align="right">Actions</TableCell>
          </TableRow>
        </TableHead>

        <TableBody>
          {items.map((t) => (
            <TableRow
              key={t.id}
              hover
              sx={{
                '&:hover': { backgroundColor: 'action.hover' },
                '&:last-child td, &:last-child th': { borderBottom: 0 },
              }}
            >
              <TableCell>
                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                    {t.ownerName}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {t.ownerPhone}
                  </Typography>
                </Box>
              </TableCell>

              <TableCell>
                <Typography variant="body2">
                  {t.carMake} {t.carModel}
                </Typography>
              </TableCell>

              <TableCell>
                <Typography variant="body2" color="text.secondary">
                  {t.carYear}
                </Typography>
              </TableCell>

              <TableCell>
                <Typography variant="body2" color="text.secondary">
                  {t.mileageKm ? `${t.mileageKm.toLocaleString()} km` : '—'}
                </Typography>
              </TableCell>

              <TableCell>
                <Typography variant="body2">
                  {t.askingPrice ? t.askingPrice.toLocaleString() : '—'}
                </Typography>
              </TableCell>

              <TableCell>
                <Chip
                  label={t.status}
                  size="small"
                  variant="outlined"
                  sx={getSoftChipSx(STATUS_CHIP_COLOR[t.status] || 'default', isDark)}
                />
              </TableCell>

              <TableCell align="right">
                <Stack direction="row" spacing={0.5} justifyContent="flex-end" flexWrap="nowrap">
                  {canManage && (
                    <>
                      <Tooltip title="Review Submission">
                        <IconButton onClick={() => onEdit(t)} size="small" color="warning">
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete Submission">
                        <IconButton
                          onClick={() => onDelete(t)}
                          disabled={deleteLoading === t.id}
                          size="small"
                          color="error"
                        >
                          {deleteLoading === t.id ? (
                            <CircularProgress size={16} />
                          ) : (
                            <DeleteIcon fontSize="small" />
                          )}
                        </IconButton>
                      </Tooltip>
                    </>
                  )}
                </Stack>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default TradeInTable;
